import { loadCommitFileRow, loadCommitFileRows } from "../commitFileRows";
import { materializeCommitStep } from "../stepTrees";
import * as z from "zod";
import { userFacingError } from "../authorization";
import { syncAllFacets } from "../commitFiles";
import { ensureWritable } from "../copyOnWrite";
import {
  isValidStepTestName,
  STEP_TEST_CASES_DIR,
} from "../stepTests/constants";
import { mutator } from "./mutator";
import { branchSchema } from "./utils";

const renameStepTestSchema = z
  .object({
    workflowId: z.string(),
    stepDir: z.string(),
    name: z.string(),
    newName: z.string(),
  })
  .merge(branchSchema);

export const renameStepTest = mutator({
  schema: renameStepTestSchema,
  resolve: {
    branch: { kind: "branch", from: "branchId" },
  },
  permissions: [
    {
      permission: "space:write",
      scopeId: ({ args }) => args.branch.workflow.spaceId,
    },
  ],
  fn: async ({ tx, ctx, args }) => {
    const {
      branch,
      stepDir,
      name,
      newName,
      forkBranchId,
      forkCommitId,
      forkChatId,
      workingCommitId,
      workingChatId,
    } = args;
    if (!isValidStepTestName(newName)) {
      throw userFacingError(
        "Step test names may only contain letters, numbers, dashes, and underscores"
      );
    }
    if (newName === name) return;
    const { commitId } = await ensureWritable(
      tx,
      ctx,
      ctx.tenantId,
      {
        branchId: branch.id,
        forkBranchId,
        forkCommitId,
        forkChatId,
        workingCommitId,
        workingChatId,
      },
      branch
    );
    const oldBase = `${stepDir}/${STEP_TEST_CASES_DIR}/${name}`;
    const newBase = `${stepDir}/${STEP_TEST_CASES_DIR}/${newName}`;
    const collision = await loadCommitFileRow(tx, commitId, `${newBase}.input`);
    if (collision) {
      throw userFacingError(`A step test named “${newName}” already exists.`);
    }
    // A test's files all share the `<base>.` prefix (`.input`, `.output`,
    // `.meta`, `.requests`, and the nested `.volumes/…`); names can't
    // contain a dot, so this can't match a sibling test. Re-point each to the
    // new base, reusing the same content-addressed blob.
    const files = (await loadCommitFileRows(tx, commitId)).filter((file) =>
      file.path.startsWith(`${oldBase}.`)
    );
    if (files.length === 0) {
      throw userFacingError(`No step test named “${name}”.`);
    }
    await materializeCommitStep(tx, commitId, `${stepDir}/config.toml`);
    for (const file of files) {
      await tx.mutate.commitFiles.insert({
        commitId,
        path: `${newBase}${file.path.slice(oldBase.length)}`,
        blobHash: file.blobHash,
        tenantId: ctx.tenantId,
      });
      await tx.mutate.commitFiles.delete({ commitId, path: file.path });
    }
    await syncAllFacets({ tx, commitId, tenantId: ctx.tenantId });
  },
});
